import { extractAccessibleRoutes } from '@/common/auth/auth.utils';
import BaseViewport from '@/common/components/templates/baseViewport/BaseViewport.vue';
import { VIEWPORT_TYPE } from '@/common/components/templates/baseViewport/baseViewport.define';
import { VIEW_MODE } from '@/common/define/common.define';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { requireAuth, setDayjsTimezone } from '@/common/router/navigationGuard';
import { requireDomainTargetOrTutorial } from '@/common/router/authGuard';
import { RouteRecordRaw } from 'vue-router';
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';

const PREFIX_APPLICATION = 'application_';

const applicationMenu: RouteRecordRaw[] = [
  {
    path: '/application/',
    name: `${PREFIX_APPLICATION}Application`,
    component: BaseViewport,
    beforeEnter: [
      requireAuth,
      () => requireDomainTargetOrTutorial('application'),
      setDayjsTimezone,
    ],
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION,
      invisibleViewMode: [VIEW_MODE.MAXGAUGE],
      viewport: VIEWPORT_TYPE.REALTIME,
    },
    children: [
      {
        path: 'apm-overview',
        name: `${PREFIX_APPLICATION}Overview`,
        component: () => import('../views/apmOverview/ApmOverview.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WAS_OVERVIEW,
        },
      },
      {
        path: 'was',
        name: `${PREFIX_APPLICATION}WAS`,
        component: () => import('../views/wasView/WasView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WAS,
        },
      },
      {
        path: 'webServer',
        name: `${PREFIX_APPLICATION}Web Server`,
        component: () => import('../views/webServerView/WebServerView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WEB_SERVER,
        },
      },
      {
        path: 'transaction',
        name: `${PREFIX_APPLICATION}Transaction`,
        component: () => import('@/application/views/TransactionView/TransactionView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_TRANSACTION,
        },
      },
      {
        path: 'urlMonitoring',
        name: `${PREFIX_APPLICATION}URL_Monitoring`,
        component: () => import('../views/urlMonitoring/UrlMonitoring.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_URL_MONITORING,
        },
      },
    ],
  },
];

const rumMenu: RouteRecordRaw[] = [
  {
    path: '/application/',
    name: `${PREFIX_APPLICATION}RUM`,
    component: BaseViewport,
    beforeEnter: [
      requireAuth,
      () => requireDomainTargetOrTutorial('application'),
      setDayjsTimezone,
    ],
    meta: {
      invisibleViewMode: [VIEW_MODE.MAXGAUGE],
      childRolePermission: [
        ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WEB_APPLICATION,
        ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_PAGE_PERFORMANCE_MONITORING,
        ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_BROWSER_ERROR_TRACKING,
        ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_USER_MONITORING,
      ],
      viewport: VIEWPORT_TYPE.REALTIME,
    },
    children: [
      {
        path: 'webApplication',
        name: `${PREFIX_APPLICATION}Web Application`,
        component: () => import('../views/webApplicationView/WebApplicationView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WEB_APPLICATION,
        },
      },
      {
        path: 'pagePerformanceMonitoring',
        name: `${PREFIX_APPLICATION}Page Performance Monitoring`,
        component: () =>
          import('../views/pagePerformanceMonitoringView/PagePerformanceMonitoringView.vue'),
        meta: {
          rolePermission:
            ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_PAGE_PERFORMANCE_MONITORING,
        },
      },
      {
        path: 'userMonitoring',
        name: `${PREFIX_APPLICATION}User Monitoring`,
        component: () => import('../views/userMonitoringView/UserMonitoringView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_USER_MONITORING,
        },
      },
      {
        path: 'browserErrorTracking',
        name: `${PREFIX_APPLICATION}Browser Error Tracking`,
        component: () => import('../views/browserErrorTracking/BrowserErrorTracking.vue'),
        meta: {
          rolePermission:
            ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_BROWSER_ERROR_TRACKING,
        },
      },
    ],
  },
];

// Application 성능 분석 메뉴
const applicationPerformanceAnalysisMenu: RouteRecordRaw[] = [
  {
    path: '/application/',
    name: `${PREFIX_APPLICATION}Performance Analysis`,
    component: BaseViewport,
    beforeEnter: [requireAuth, setDayjsTimezone],
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_ANALYSIS,
      invisibleViewMode: [VIEW_MODE.MAXGAUGE],
      viewport: VIEWPORT_TYPE.PA,
    },
    children: [
      {
        path: 'performance-analysis/transaction-trend',
        name: `${PREFIX_APPLICATION}Transaction Trend`,
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
        component: () => import('../views/transactionTrend/TransactionTrend.vue'),
      },
      {
        path: 'performance-analysis/exception-summary',
        name: `${PREFIX_APPLICATION}Exception Summary`,
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
        component: () => import('../views/exceptionSummary/ExceptionSummary.vue'),
      },
      {
        path: 'performance-analysis/trend-analysis',
        name: `${PREFIX_APPLICATION}Trend Analysis`,
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
        component: () => import('../views/trendAnalysis/TrendAnalysis.vue'),
      },
      {
        path: 'performance-analysis/top-transaction-summary',
        name: `${PREFIX_APPLICATION}Top Transaction Summary`,
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
        component: () => import('../views/topTxnSummary/TopTxnSummary.vue'),
      },
      {
        path: 'performance-analysis/top-sql-summary',
        name: `${PREFIX_APPLICATION}Top SQL Summary`,
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
        component: () => import('../views/topSqlSummary/TopSqlSummary.vue'),
      },
      {
        path: 'performance-analysis/performance-heatmap',
        name: `${PREFIX_APPLICATION}Performance Heatmap`,
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
        component: () => import('../views/performanceHeatmap/PerformanceHeatmap.vue'),
      },
      {
        path: 'performance-analysis/thread-dump',
        name: `${PREFIX_APPLICATION}Thread Dump`,
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
        component: () => import('../views/threadDump/ThreadDump.vue'),
      },
      {
        path: 'performance-analysis/agent-workload',
        name: `${PREFIX_APPLICATION}Agent Workload`,
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
        component: () => import('../views/agentWorkload/AgentWorkload.vue'),
      },
      {
        path: 'performance-analysis/daily-report',
        name: `${PREFIX_APPLICATION}Daily Report`,
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
        component: () => import('../views/dailyReport/DailyReport.vue'),
      },
      {
        path: 'performance-analysis/gc-log-analyzer',
        name: `${PREFIX_APPLICATION}GC Log Analyzer`,
        meta: {
          category: BASE_MENU_VALUE.PA,
        },
        component: () => import('../views/gcLogAnalysis/GcLogAnalysis.vue'),
      },
    ],
  },
];

const getApplicationNaviMenu = () => {
  return extractAccessibleRoutes(applicationMenu);
};

const getRumNaviMenu = () => {
  return extractAccessibleRoutes(rumMenu);
};

const getApplicationPerformanceAnalysisMenu = () => {
  return extractAccessibleRoutes(applicationPerformanceAnalysisMenu);
};

export {
  PREFIX_APPLICATION,
  applicationMenu,
  rumMenu,
  applicationPerformanceAnalysisMenu,
  getApplicationNaviMenu,
  getRumNaviMenu,
  getApplicationPerformanceAnalysisMenu,
};
